
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import GlobalLayout from './GlobalLayout';
import LanguageSwitcher from './LanguageSwitcher';
import { useNotificationsContext } from './NotificationsContext';
import './Profile.css';
import defaultUserIcon from '../assets/icons/users.svg';
import defaultAchievementIcon from '../assets/icons/trophy.svg';

const ADMIN_ROLES = [0, 2, 3];

const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  const localizedField = `${field}_${code}`;
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }
  return item[field] || '';
};

const Profile = () => {
  const navigate = useNavigate();
  const { logout, getUser, makeAuthenticatedRequest } = useAuth();
  
  const { t, languageName, getAvailableLanguages, setLanguageFromId, setContextLanguages } = useLanguage();

  const { notifications: unreadNotifs, dismissOne, dismissAll } = useNotificationsContext();
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [selectedNotif,  setSelectedNotif]  = useState(null);
  // allNotifs: fetched once on modal open, kept with local read flag
  const [allNotifs,      setAllNotifs]      = useState(null); // null = not yet loaded
  const [notifLoading,   setNotifLoading]   = useState(false);
  // Track which IDs were read in this session
  const [readIds,        setReadIds]        = useState(() => new Set());

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.replace(' ', 'T'));
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1)  return 'Gerade eben';
    if (diffMin < 60) return `${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24)   return `${diffH} h`;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const loadAllNotifs = useCallback(async () => {
    if (notifLoading) return;
    setNotifLoading(true);
    try {
      const raw = await makeAuthenticatedRequest('/notifications/all');
      // makeAuthenticatedRequest may return a raw Response or already-parsed JSON
      const res = (raw && typeof raw.json === 'function') ? await raw.json() : raw;
      const data = res?.data ?? (Array.isArray(res) ? res : []);
      setAllNotifs(data);
    } catch (e) {
      console.error('[Profile] loadAllNotifs error:', e);
      setAllNotifs([...unreadNotifs].map(n => ({ ...n, is_read: false })));
    } finally {
      setNotifLoading(false);
    }
  }, [makeAuthenticatedRequest, unreadNotifs, notifLoading]);

  const handleOpenModal = () => {
    setShowNotifModal(true);
    setSelectedNotif(null);
    loadAllNotifs();
  };

  const handleCloseModal = () => {
    setShowNotifModal(false);
    setSelectedNotif(null);
  };

  const handleSelectNotif = (n) => {
    const isUnread = !readIds.has(n.id) && unreadNotifs.some(u => u.id === n.id);
    if (isUnread) {
      dismissOne(n.id);
      setReadIds(prev => new Set([...prev, n.id]));
    }
    setSelectedNotif(n);
  };
  
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfileData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
  
      const data = await getUser();
      
      // Wrapper Handling
      const userObj = (data && data.data) ? data.data : data;

      if (!userObj || typeof userObj !== 'object') {
        throw new Error('No data received from API');
      }

      if (data && Array.isArray(data.available_languages) && data.available_languages.length > 0) {
          setContextLanguages(data.available_languages);
      }

      const apiAchievements = Array.isArray(data?.achievements) ? data.achievements : [];
      const apiUserLevel = data?.userlevel?.name || null;

      const getYearFromDateString = (value) => {
        if (!value) return new Date().getFullYear();
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
      };

      if (userObj && userObj.preferred_language) {
          setTimeout(() => {
             setLanguageFromId(userObj.preferred_language);
          }, 50);
      }
      
      const transformedData = {
        id: userObj.hash_id || userObj.id || 'N/A',
        name: userObj.name || 'Unknown User',
        status: userObj.status || 0,
        avatar: userObj.avatar || '',
        xp: userObj.xp || 0,
        // 'full' = alle Features; 'essentials' = kein Content-Creation (video_builder etc.)
        seatType: userObj.seat_type === 'full' ? 'full' : 'essentials',
        // users.group = Systemrolle (0/1/2/3) — für Admin-Zugangskontrolle
        systemGroup: userObj.group ?? userObj.system_group ?? null,
        groups: Array.isArray(userObj.groups) ? userObj.groups : (userObj.group ? [{ id: userObj.group, name: String(userObj.group) }] : []),
        clients: Array.isArray(userObj.clients) ? userObj.clients : (userObj.client_id ? [{ id: userObj.client_id, name: String(userObj.client_id), status: 1 }] : []),
        progress: Array.isArray(userObj.progress) ? userObj.progress : [],
        stats: calculateStats(userObj.progress || []),
        level: apiUserLevel || determineUserLevel(userObj.groups || [], userObj.progress || []),
        joinedYear: getYearFromDateString(userObj.created || userObj.last_update),
        achievements: (apiAchievements.length > 0)
          ? apiAchievements.map(a => ({
              name:    a.name    || 'Achievement',
              name_en: a.name_en || '',
              name_fr: a.name_fr || '',
              name_es: a.name_es || '',
              name_ru: a.name_ru || '',
              name_pl: a.name_pl || '',
              name_ar: a.name_ar || '',
              name_ro: a.name_ro || '',
              name_tr: a.name_tr || '',
              name_hr: a.name_hr || '',
              avatar:  a.avatar  || ''
            }))
          : generateAchievements(userObj.progress || [])
      };
      
      setProfileData(transformedData);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      setProfileData({
        name: 'User',
        id: '0000',
        level: 'Beginner',
        joinedYear: '2024',
        avatar: '',
        progress: [],
        groups: [],
        clients: [],
        stats: { totalCourses: 0, completed: 0, inProgress: 0 },
        achievements: [],
        status: 1,
        systemGroup: null,
      });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // getUser kommt aus Context mit stabiler Referenz (useCallback in AuthContext).
  // Dep-Array [] verhindert, dass fetchProfileData bei jedem AuthProvider-Re-render
  // eine neue Referenz bekommt und damit den useEffect unten neu auslöst (Loop-Breaker).

    
  useEffect(() => {
    fetchProfileData(true); 
  }, [fetchProfileData]);


  const calculateStats = (progressData) => {
    const completed = progressData.filter(p => p.progress === 100).length;
    const inProgress = progressData.filter(p => p.progress > 0 && p.progress < 100).length;
    const totalCourses = progressData.length;
    return { totalCourses, completed, inProgress };
  };

  const determineUserLevel = (groups, progressData) => {
    if (groups.length > 0) {
      const groupName = groups[0].name.toLowerCase();
      if (groupName.includes('advanced')) return 'Advanced';
      if (groupName.includes('intermediate')) return 'Intermediate';
      if (groupName.includes('beginner')) return 'Beginner';
    }
    const completedCount = progressData.filter(p => p.progress === 100).length;
    if (completedCount >= 10) return 'Advanced';
    if (completedCount >= 5) return 'Intermediate';
    return 'Beginner';
  };

  const generateAchievements = (progressData) => {
    return [];
  };

  const calculateOverallProgress = (progressData) => {
    if (progressData.length === 0) return 0;
    const totalProgress = progressData.reduce((sum, p) => sum + p.progress, 0);
    return Math.round(totalProgress / progressData.length);
  };

  const handleLogout = () => {
    logout();
  };

  // Admin-Zugangskontrolle:
  // Priorisiere profileData.systemGroup (vom API-Response).
  // Fallback: localStorage, falls der /user-Endpunkt users.group noch nicht mitschickt.
  const resolveAdminAccess = () => {
    if (profileData?.systemGroup !== null && profileData?.systemGroup !== undefined) {
      return ADMIN_ROLES.includes(Number(profileData.systemGroup));
    }
    try {
      const stored = JSON.parse(localStorage.getItem('hejvi_user') || '{}');
      return ADMIN_ROLES.includes(Number(stored?.group ?? 99));
    } catch {
      return false;
    }
  };

  if (loading) {
    return (
      <GlobalLayout title={t('myProfile')}>
        <div className="loading-state" style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div className="spinner"></div>
          <p>{t('loading')}</p>
        </div>
      </GlobalLayout>
    );
  }

  const isAdmin = resolveAdminAccess();

  return (
    <GlobalLayout title={t('myProfile')}>
      {/* User Info Section */}
      <div className="user-info-section" style={{ paddingTop: '80px' }}>
        <img
          className="user-avatar"
          src={profileData?.avatar || defaultUserIcon}
          onError={(e) => {
            if (e.currentTarget.src !== window.location.origin + defaultUserIcon) {
              e.currentTarget.src = defaultUserIcon;
            }
          }}
          alt={profileData?.name || 'User avatar'}
        />
        <div className="user-details">
          <h2 className="user-name">{profileData?.name || 'Loading...'}</h2>
          <p className="user-id">ID: {profileData?.id || 'Loading...'}</p>
          {profileData?.groups && profileData.groups.length > 0 && (
            <p className="user-group">{t('group')}: {profileData.groups[0].name}</p>
          )}
          {profileData?.clients && profileData.clients.length > 0 && (
            <p className="user-client">{t('client')}: {profileData.clients[0].name}</p>
          )}
        </div>
        <div className="user-badges">
          <span className="badge beginner-badge">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 0L8.5 4.5L12 5L9 8L10 12L7 10L4 12L5 8L2 5L5.5 4.5L7 0Z" fill="currentColor"/>
            </svg>
            {t(profileData?.level?.toLowerCase() || 'beginner')}
          </span>
          <span className="badge joined-badge">
            <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
              <path d="M6 0L7.5 3.5L11 4L8 7L9 10.5L6 8.5L3 10.5L4 7L1 4L4.5 3.5L6 0Z" fill="currentColor"/>
            </svg>
            {t('joined')} {profileData?.joinedYear || '2024'}
          </span>
          {profileData?.status === 1 && (
            <span className="badge status-badge active">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="6" fill="#10B981"/>
              </svg>
              {t('active')}
            </span>
          )}
        </div>
      </div>

      {/* ── Nachrichten-Button ── */}
      <div className="notif-entry-container">
        <button className="notif-entry-btn" onClick={handleOpenModal}>
          <div className="notif-entry-left">
            <div className="notif-entry-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <span className="notif-entry-label">{t('notif_title')}</span>
          </div>
          <div className="notif-entry-right">
            {unreadNotifs.length > 0 && (
              <span className="notif-entry-badge">{unreadNotifs.length}</span>
            )}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
        </button>
      </div>

      {/* ── Nachrichten-Modal ── */}
      {showNotifModal && (
        <div className="notif-modal-overlay" onClick={handleCloseModal}>
          <div className="notif-modal" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="notif-modal-header">
              <h3 className="notif-modal-title">{t('notif_title')}</h3>
              <button className="notif-modal-close" onClick={handleCloseModal}>✕</button>
            </div>

            {/* Detail view */}
            {selectedNotif ? (
              <div className="notif-detail">
                <button className="notif-back-btn" onClick={() => setSelectedNotif(null)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                  {t('notif_back')}
                </button>
                <div className="notif-detail-body">
                  <p className="notif-detail-title">{selectedNotif.title}</p>
                  <p className="notif-detail-text">{selectedNotif.body}</p>
                </div>

                {/* Admin-Panel-Link für unbeantwortete Fragen */}
                {(selectedNotif.type === 'unanswered_question' ||
                  (selectedNotif.type === 'admin_message' && selectedNotif.collection_hash === 'fraghejvi')) && (
                  <div style={{ padding: '12px 0 4px' }}>
                    <button
                      onClick={() => {
                        handleCloseModal();
                        sessionStorage.setItem('hejvi_admin_tab', 'fraghejvi');
                        navigate('/admin');
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        width: '100%',
                        background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
                        border: 'none', borderRadius: 12,
                        color: '#fff', fontSize: 14, fontWeight: 700,
                        padding: '13px 20px', cursor: 'pointer',
                        boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      {t('notif_goto_adminpanel') || 'Zum Admin-Panel'}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* List view */
              <div className="notif-list">
                {notifLoading ? (
                  <p className="notif-empty">{t('notif_loading')}</p>
                ) : !allNotifs || allNotifs.length === 0 ? (
                  <p className="notif-empty">{t('notif_empty')}</p>
                ) : (
                  allNotifs.map(n => {
                    const isUnread = !n.is_read && !readIds.has(n.id);
                    return (
                      <button
                        key={n.id}
                        className={`notif-list-item${isUnread ? ' notif-list-item--unread' : ''}`}
                        onClick={() => handleSelectNotif(n)}
                      >
                        <div className="notif-list-item-inner">
                          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                            <p className={`notif-item-title${isUnread ? ' notif-item-title--unread' : ''}`}>
                              {n.title}
                            </p>
                            <span style={{ fontSize: '10px', color: '#C4B5A5', flexShrink: 0, fontFamily: 'inherit' }}>
                              {fmtDate(n.created_at)}
                            </span>
                          </div>
                          <p className="notif-item-body">
                            {n.body?.length > 100 ? n.body.slice(0, 100) + '…' : n.body}
                          </p>
                        </div>
                        <svg className="notif-item-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress Section */}
      <div className="progress-section">
        <div className="progress-header">
          <h3>{t('myProgress')}</h3>
          <div className="progress-percentage">
            <svg className="progress-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {profileData ? calculateOverallProgress(profileData.progress) : 0}%
          </div>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${profileData ? calculateOverallProgress(profileData.progress) : 0}%` }}
            ></div>
          </div>
        </div>
        <div className="stats-container">
          <div className="stat-item">
            <span className="stat-number courses">{profileData?.stats?.totalCourses || 0}</span>
            <span className="stat-label">{t('courses')}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number completed">{profileData?.stats?.completed || 0}</span>
            <span className="stat-label">{t('completed')}</span>
          </div>
          <div className="stat-item">
            <span className="stat-number in-progress">{profileData?.stats?.inProgress || 0}</span>
            <span className="stat-label">{t('inProgress')}</span>
          </div>
        </div>
        {/* XP Anzeige */}
        <div className="xp-display" style={{ marginTop: '16px', padding: '0 4px' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #FF6407 0%, #FF8A3D 100%)', 
            borderRadius: '12px', 
            padding: '16px 20px', 
            color: 'white', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            boxShadow: '0 4px 12px rgba(255, 100, 7, 0.2)',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ 
                background: 'rgba(255,255,255,0.2)', 
                width: '42px', 
                height: '42px', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                ⚡
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.9, fontWeight: 600 }}>{t('totalXp')}</span>
                <span style={{ fontSize: '22px', fontWeight: '800', lineHeight: 1.1 }}>
                  {(() => {
                  const xp = profileData?.xp || 0;
                  if (xp >= 10000) {
                    return (xp / 1000).toFixed(1) + ' K';
                  }
                  return new Intl.NumberFormat('de-DE').format(xp);
                })()}
                </span>
              </div>
            </div>
            <div style={{ opacity: 0.8 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
  
        {/* 1. CREATE VIDEO — nur für Full-Licence Nutzer und Admins */}
        {(profileData?.seatType === 'full' || [0, 2, 3].includes(Number(profileData?.systemGroup))) && (
        <button className="action-button" onClick={() => navigate('/create-element')}>
          <div className="action-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM9.5 15.5V8.5L15.5 12L9.5 15.5Z" fill="white"/>
            </svg>
          </div>
          <span>{t('createElement')}</span>
        </button>
        )}

        {/* 2. CREATE COURSE — nur für Full-Licence Nutzer und Admins */}
        {(profileData?.seatType === 'full' || [0, 2, 3].includes(Number(profileData?.systemGroup))) && (
        <button className="action-button" onClick={() => navigate('/create-course')}>
          <div className="action-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6H2V18C2 19.1 2.9 20 4 20H18V18H4V6Z" fill="white"/>
              <path d="M20 2H8C6.9 2 6 2.9 6 4V14C6 15.1 6.9 16 8 16H20C21.1 16 22 15.1 22 14V4C22 2.9 21.1 2 20 2ZM18 11H8V9H18V11ZM18 7H8V5H18V7Z" fill="white"/>
            </svg>
          </div>
          <span>{t('createCourse')}</span>
        </button>
        )}

        {/* 3. CREATE QUIZ — nur für Full-Licence Nutzer und Admins */}
        {(profileData?.seatType === 'full' || [0, 2, 3].includes(Number(profileData?.systemGroup))) && (
        <button className="action-button" onClick={() => navigate('/create-quiz')}>
          <div className="action-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="white"/>
            </svg>
          </div>
          <span>{t('createQuiz')}</span>
        </button>
        )}
      
      </div>

      {/* ISO-Code der aktuellen Sprache ermitteln */}
      {(() => { const _langs = getAvailableLanguages(); const _l = _langs.find(l => l.name === languageName); window._hejviLangCode = _l?.code || _l?.language_code || 'de'; })()}

      {/* Achievements Section */}
      <div className="achievements-section">
        <div className="achievements-header">
          <h3>{t('achievements')}</h3>
          <button className="see-all-button" style={{ visibility: 'hidden' }}>See All</button>
        </div>
          {profileData.achievements && profileData.achievements.length > 0 ? (
              <div className="achievements-grid">
                {profileData.achievements.map((ach, index) => (
                  <div key={index} className="achievement-card fade-in" style={{animationDelay: `${index * 0.1}s`}}>
                    <div className="achievement-icon">
                      <img src={ach.icon_url || defaultAchievementIcon} alt="Achievement" />
                    </div>
                    <div className="achievement-info">
                      <h4>{(() => {
                        const langs = getAvailableLanguages();
                        const lang = langs.find(l => l.name === languageName);
                        const code = lang?.code || lang?.language_code || 'de';
                        return getLocalizedContent(ach, 'name', code);
                      })()}</h4>
                      <p>{ach.description}</p>
                    </div>
                  </div>
                ))}
              </div>
          ) : (
              <div className="no-content-placeholder" style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>
                  {t('none') || 'Noch keine'}
              </div>
          )}
      </div>

      {/* Settings Section */}
      <div className="settings-section">
        <div className="language-setting">
          <div className="setting-header">
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path d="M8.5 0C3.81 0 0 3.81 0 8.5S3.81 17 8.5 17S17 13.19 17 8.5S13.19 0 8.5 0Z" fill="currentColor"/>
            </svg>
            <h3>{t('language')}</h3>
          </div>
          <LanguageSwitcher />
        </div>

        <div className="settings-menu">
          <button className="setting-item" onClick={() => navigate('/error')} style={{ display: 'none' }}>
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none">
              <path d="M8.5 0L10.5 2L8.5 4L6.5 2L8.5 0ZM0 8.5L2 6.5L4 8.5L2 10.5L0 8.5ZM13 8.5L15 6.5L17 8.5L15 10.5L13 8.5ZM8.5 13L10.5 15L8.5 17L6.5 15L8.5 13Z" fill="currentColor"/>
            </svg>
            {t('settings')}
          </button>
          <button className="setting-item" onClick={() => navigate('/error')} style={{ display: 'none' }}>
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none">
              <path d="M8.5 0L10.5 2L8.5 4L6.5 2L8.5 0ZM0 8.5L2 6.5L4 8.5L2 10.5L0 8.5ZM13 8.5L15 6.5L17 8.5L15 10.5L13 8.5ZM8.5 13L10.5 15L8.5 17L6.5 15L8.5 13Z" fill="currentColor"/>
            </svg>
            {t('privacy')}
          </button>
          <button className="setting-item" onClick={() => navigate('/error')} style={{ display: 'none' }}>
            <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path d="M8.5 0C3.81 0 0 3.81 0 8.5S3.81 17 8.5 17S17 13.19 17 8.5S13.19 0 8.5 0Z" fill="currentColor"/>
            </svg>
            {t('help')}
          </button>
          <button className="setting-item logout-item" onClick={handleLogout}>
            <svg width="17" height="15" viewBox="0 0 17 15" fill="none">
              <path d="M6 14H2C1.45 14 1 13.55 1 13V2C1 1.45 1.45 1 2 1H6V3H3V12H6V14ZM11.5 10L10.09 8.59L12.17 6.5H6V4.5H12.17L10.09 2.41L11.5 1L16 5.5L11.5 10Z" fill="currentColor"/>
            </svg>
            {t('logout')}
          </button>
        </div>
      </div>

      {/* ── Admin-Zugang ─────────────────────────────────────────────────────── */}
      {isAdmin && (
        <div className="settings-section settings-section--admin">
          <div className="settings-menu">
            <button className="setting-item admin-item" onClick={() => navigate('/admin')}>
              {/* Shield icon — Admin */}
              <svg width="16" height="17" viewBox="0 0 16 17" fill="none">
                <path d="M8 0.5L1 3.5V8C1 11.86 4.08 15.49 8 16.5C11.92 15.49 15 11.86 15 8V3.5L8 0.5Z" fill="currentColor" opacity="0.15"/>
                <path d="M8 0.5L1 3.5V8C1 11.86 4.08 15.49 8 16.5C11.92 15.49 15 11.86 15 8V3.5L8 0.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                <path d="M5.5 8.5L7 10L10.5 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('adminArea') || 'Adminbereich'}
            </button>
          </div>
        </div>
      )}

    </GlobalLayout>
  );
};

export default Profile;
